import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// ═══════════════════════════════════════════════════════════
// 构造解释: 断层面 + 层位面 (地震解释成果, 非地震体数据)
//
// 设计取舍 —— 为什么不导入地震体:
//   一个中等三维工区的叠后 SEG-Y 约 500×500 道 × 1000 样点 × 4 字节 ≈ 1 GB,
//   而 WebGL 3D 纹理实际可用量级为 256³(≈16MB)~512³(≈128MB)。
//   因此本模块只承载**解释成果**: 断层面(产状+延展, 几 KB)、
//   层位面(网格化 Z 值, 500×500 ≈ 1 MB), 两者都可直接在浏览器渲染。
// ═══════════════════════════════════════════════════════════

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  border: '#D6DAE0', panelBg: '#F4F6F9', accent: '#C2533A', blue: '#2874A6',
};

type Fault = {
  id: number; name: string; ref_east: number; ref_north: number; ref_tvd: number;
  strike: number; dip_angle: number; dip_azimuth: number; throw_m: number | null;
  extent_along_strike: number; extent_along_dip: number; is_sealing: boolean; severity: number;
};
type Horizon = {
  id: number; name: string; nx: number; ny: number;
  dx: number; dy: number; origin_east: number; origin_north: number; color_code: string;
};

const BLANK_FAULT = {
  name: '', ref_east: 0, ref_north: 0, ref_tvd: 3000,
  strike: 0, dip_angle: 70, dip_azimuth: 90, throw_m: 20,
  extent_along_strike: 1200, extent_along_dip: 800, is_sealing: false, severity: 3,
};

export default function StructurePage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;
  const [faults, setFaults] = useState<Fault[]>([]);
  const [horizons, setHorizons] = useState<Horizon[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>(BLANK_FAULT);
  const [adding, setAdding] = useState(false);

  const un = (d: any) => (Array.isArray(d) ? d : d?.results ?? []);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [f, h] = await Promise.all([
        formationAPI.listFaults(projectId),
        formationAPI.listHorizons(projectId),
      ]);
      setFaults(un(f.data));
      setHorizons(un(h.data));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (projectId) load(); }, [projectId]);

  const addFault = async () => {
    if (!projectId) return;
    if (!form.name.trim()) { message.warning('请填写断层名称'); return; }
    try {
      await formationAPI.createFault({ ...form, project: projectId });
      message.success('已添加');
      setForm(BLANK_FAULT); setAdding(false);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '添加失败');
    }
  };

  const delFault = async (id: number, name: string) => {
    if (!window.confirm(`确认删除断层「${name}」？`)) return;
    try {
      await formationAPI.deleteFault(id);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '删除失败');
    }
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  const num = (k: string, label: string, step = 1) => (
    <label style={{ fontSize: 11, color: TH.textSec, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {label}
      <input type="number" step={step} value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })}
        style={{ width: 92, fontSize: 12, padding: '3px 6px', borderRadius: 3, border: `1px solid ${TH.border}` }} />
    </label>
  );

  return (
    <div style={{ padding: 20, fontFamily: 'inherit' }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: TH.text, margin: '0 0 3px' }}>层位与断层</h2>
      <div style={{ fontSize: 12, color: TH.textMuted, marginBottom: 14 }}>
        地震构造解释成果。断层以产状(走向/倾角/倾向)+延展定义，可在 3D 模型中按真实倾斜面成图。
      </div>

      {/* ── 断层面 ── */}
      <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6, marginBottom: 18 }}>
        <div style={{ padding: '8px 14px', background: TH.panelBg, borderBottom: `1px solid ${TH.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: TH.text }}>断层面 ({faults.length})</span>
          <button onClick={() => setAdding((v) => !v)}
            style={{ fontSize: 12, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${TH.accent}`, background: adding ? TH.accent : '#fff', color: adding ? '#fff' : TH.accent }}>
            {adding ? '取消' : '+ 新增断层'}
          </button>
        </div>

        {adding && (
          <div style={{ padding: 14, borderBottom: `1px solid ${TH.border}`, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label style={{ fontSize: 11, color: TH.textSec, display: 'flex', flexDirection: 'column', gap: 3 }}>
              断层名称
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="F1" style={{ width: 110, fontSize: 12, padding: '3px 6px', borderRadius: 3, border: `1px solid ${TH.border}` }} />
            </label>
            {num('ref_east', '参考点东(m)', 10)}
            {num('ref_north', '参考点北(m)', 10)}
            {num('ref_tvd', '参考点垂深(m)', 10)}
            {num('strike', '走向(°)')}
            {num('dip_angle', '倾角(°)')}
            {num('dip_azimuth', '倾向(°)')}
            {num('throw_m', '断距(m)')}
            {num('extent_along_strike', '沿走向延展(m)', 50)}
            {num('extent_along_dip', '沿倾向延展(m)', 50)}
            {num('severity', '风险等级1-5')}
            <label style={{ fontSize: 11, color: TH.textSec, display: 'flex', alignItems: 'center', gap: 5 }}>
              <input type="checkbox" checked={form.is_sealing}
                onChange={(e) => setForm({ ...form, is_sealing: e.target.checked })} />
              封闭断层
            </label>
            <button onClick={addFault}
              style={{ fontSize: 12, padding: '6px 18px', borderRadius: 4, fontWeight: 600, cursor: 'pointer', border: 'none', background: TH.accent, color: '#fff' }}>
              保存
            </button>
          </div>
        )}

        {!faults.length ? (
          <div style={{ padding: 28, textAlign: 'center', color: TH.textMuted, fontSize: 12 }}>
            {loading ? '加载中…' : '暂无断层数据。点「+ 新增断层」录入地震解释成果。'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                {['名称', '走向(°)', '倾角(°)', '倾向(°)', '断距(m)', '延展 走向×倾向(m)', '参考点垂深(m)', '封闭', '风险', ''].map((h) => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.border}`, color: TH.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {faults.map((f) => (
                <tr key={f.id}>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontWeight: 600 }}>{f.name}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{f.strike}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{f.dip_angle}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{f.dip_azimuth}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{f.throw_m ?? '—'}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace', color: TH.textSec }}>
                    {f.extent_along_strike} × {f.extent_along_dip}
                  </td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{f.ref_tvd}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5' }}>{f.is_sealing ? '是' : '否'}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5' }}>
                    <span style={{ color: f.severity >= 4 ? '#C0392B' : f.severity >= 3 ? '#D4770C' : TH.textSec }}>{f.severity}</span>
                  </td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5' }}>
                    <button onClick={() => delFault(f.id, f.name)}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, cursor: 'pointer', border: `1px solid ${TH.border}`, background: '#fff', color: TH.textSec }}>删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 层位面 ── */}
      <div style={{ background: '#fff', border: `1px solid ${TH.border}`, borderRadius: 6 }}>
        <div style={{ padding: '8px 14px', background: TH.panelBg, borderBottom: `1px solid ${TH.border}`, fontSize: 13, fontWeight: 600, color: TH.text }}>
          层位面 ({horizons.length})
        </div>
        {!horizons.length ? (
          <div style={{ padding: '20px 24px', color: TH.textSec, fontSize: 12, lineHeight: 2 }}>
            暂无层位面数据。
            <div style={{ color: TH.textMuted, marginTop: 6 }}>
              层位面为网格化的构造解释成果（每个网格点一个 TVD 值），可通过接口
              <code style={{ margin: '0 4px' }}>POST /api/v1/formation/horizons/</code>
              导入，字段为 <code>nx / ny / dx / dy / origin_east / origin_north / grid_z</code>。
              <br />
              规模建议控制在 40 万网格点以内（约 500×500 ≈ 1 MB），超出会被后端拒绝——
              浏览器端渲染与传输都不适合更大的面。
              <br />
              <b style={{ color: TH.textSec }}>地震体数据（SEG-Y）不在本模块范围内</b>：
              叠后体量级约 1 GB，远超浏览器 WebGL 可承载的 3D 纹理规模，应在专业解释软件中处理后，
              只把层位、断层、沿层属性等解释成果导入本平台。
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFBFC' }}>
                {['名称', '网格 nx×ny', '间距 dx×dy(m)', '网格点数', '原点(东,北)', ''].map((h) => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.border}`, color: TH.textSec, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {horizons.map((h) => (
                <tr key={h.id}>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontWeight: 600 }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: h.color_code, marginRight: 6 }} />
                    {h.name}
                  </td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{h.nx} × {h.ny}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{h.dx} × {h.dy}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace' }}>{(h.nx * h.ny).toLocaleString()}</td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5', fontFamily: 'monospace', color: TH.textSec }}>
                    ({h.origin_east}, {h.origin_north})
                  </td>
                  <td style={{ padding: '5px 10px', borderBottom: '1px solid #F0F2F5' }}>
                    <button onClick={async () => {
                      if (!window.confirm(`确认删除层位面「${h.name}」？`)) return;
                      await formationAPI.deleteHorizon(h.id); await load();
                    }} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, cursor: 'pointer', border: `1px solid ${TH.border}`, background: '#fff', color: TH.textSec }}>删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
